import React, { useCallback, useEffect, useRef, useState } from "react";
import {
    Alert,
    Box,
    Button,
    Chip,
    CircularProgress,
    Dialog,
    DialogContent,
    DialogTitle,
    Divider,
    FormControl,
    Grid,
    IconButton,
    InputLabel,
    MenuItem,
    Select,
    Stack,
    TextField,
    Tooltip,
    Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import UploadFileIcon from "@mui/icons-material/UploadFile";
import AutorenewIcon from "@mui/icons-material/Autorenew";
import moment from "moment";
import Swal from "sweetalert2";
import documentServices from "app/services/document-services";
import SignatureCanvas from "app/widgets/Shared/SignatureCanvas";

// Una de las dos mitades del acuse: el documento sin firmar o el firmado.
//
// Las dos se dibujan igual porque para el que mira son la misma pregunta hecha dos veces
// ("¿está?, ¿de dónde salió?, ¿lo puedo ver?"). Lo único que cambia es qué se puede hacer con
// cada una, y eso lo decide el backend en `destroyable`: la copia firmada que salió de la app
// móvil es la prueba de entrega y no se borra desde acá.
const DocumentSlot = ({ title, slotKey, doc, busy, onUpload, onDelete }) => {
    const inputRef = useRef(null);

    return (
        <Box sx={{ border: 1, borderColor: "divider", borderRadius: 2, p: 2, height: "100%" }}>
            <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1 }}>
                <Typography variant="subtitle2">{title}</Typography>
                <Chip
                    size="small"
                    label={doc?.present ? "Disponible" : "Falta"}
                    color={doc?.present ? "success" : "default"}
                />
            </Stack>

            <Typography variant="caption" color="text.secondary" display="block">
                Origen: {doc?.origin || "—"}
            </Typography>

            {doc?.warning && <Alert severity="warning" sx={{ mt: 1, py: 0 }}>{doc.warning}</Alert>}
            {doc?.error && <Alert severity="error" sx={{ mt: 1, py: 0 }}>{doc.error}</Alert>}

            <Stack direction="row" spacing={1} sx={{ mt: 1.5 }}>
                {doc?.present && doc?.file_url && (
                    <Button
                        size="small"
                        variant="outlined"
                        startIcon={<OpenInNewIcon />}
                        // La URL viene firmada por S3 y dura pocos minutos: se abre en el momento,
                        // no se guarda ni se comparte.
                        href={doc.file_url}
                        target="_blank"
                        rel="noopener noreferrer"
                    >
                        Ver PDF
                    </Button>
                )}

                <Button
                    size="small"
                    variant="text"
                    startIcon={<UploadFileIcon />}
                    disabled={busy}
                    onClick={() => inputRef.current?.click()}
                >
                    {doc?.present ? "Reemplazar" : "Subir"}
                </Button>

                {doc?.present && doc?.destroyable && (
                    <Tooltip title="Borrar">
                        <span>
                            <IconButton size="small" color="error" disabled={busy} onClick={() => onDelete(doc.id)}>
                                <DeleteOutlineIcon fontSize="small" />
                            </IconButton>
                        </span>
                    </Tooltip>
                )}
            </Stack>

            <input
                ref={inputRef}
                type="file"
                accept="application/pdf"
                hidden
                onChange={(event) => {
                    const file = event.target.files?.[0];
                    // El input se limpia siempre: sin esto, subir el mismo archivo dos veces
                    // seguidas no dispara onChange la segunda vez.
                    event.target.value = "";
                    if (file) onUpload(slotKey, file);
                }}
            />
        </Box>
    );
};

const DeliveryDocumentsDialog = ({ deliveryId, onClose, onChanged }) => {
    const [loading, setLoading] = useState(false);
    const [busy, setBusy] = useState(false);
    const [detail, setDetail] = useState(null);

    const [regularizationType, setRegularizationType] = useState("");
    const [regularizationNumber, setRegularizationNumber] = useState("");

    const load = useCallback(() => {
        if (!deliveryId) return;

        setLoading(true);
        documentServices.getDeliveryDocuments(deliveryId)
            .then((data) => {
                setDetail(data);
                setRegularizationType(data?.regularization?.documentType || "");
                setRegularizationNumber(data?.regularization?.documentNumber || "");
            })
            .finally(() => setLoading(false));
    }, [deliveryId]);

    useEffect(() => { load(); }, [load]);

    // El backend devuelve los documentos indexados por su título ("Recepción conforme") en vez de
    // por una clave fija, que es como los dibujaba la versión de lubrax. Se recorre en vez de
    // buscar por nombre: si mañana se agrega otro tipo de documento, aparece solo.
    const documentEntries = Object.entries(detail?.documents || {});

    // La firma llega como el JSON de trazos que guarda la app. Si viene rota o vacía no se
    // dibuja nada: una firma ilegible no puede tumbar el pop-up entero.
    const signatureStrokes = React.useMemo(() => {
        const raw = detail?.receipt?.signature;
        if (!raw) return null;

        try {
            const parsed = typeof raw === "string" ? JSON.parse(raw) : raw;
            const usable = (Array.isArray(parsed) ? parsed : []).filter((stroke) => stroke?.points?.length > 1);
            return usable.length ? usable : null;
        } catch (error) {
            return null;
        }
    }, [detail]);

    const refresh = (payload) => {
        if (payload?.documents) {
            setDetail((previous) => ({ ...previous, documents: payload.documents }));
        }
        if (onChanged) onChanged();
    };

    const handleUpload = (slot, file) => {
        setBusy(true);
        documentServices.uploadDocument(deliveryId, file, slot)
            .then(refresh)
            .finally(() => setBusy(false));
    };

    const handleDelete = (shippingDocId) => {
        Swal.fire({
            title: "¿Borrar este documento?",
            text: "Se elimina el PDF de esta entrega. Solo se pueden borrar los que se subieron a mano.",
            icon: "warning",
            showCancelButton: true,
            confirmButtonText: "Borrar",
            cancelButtonText: "Cancelar",
        }).then((result) => {
            if (!result.isConfirmed) return;

            setBusy(true);
            documentServices.destroyDocument(deliveryId, shippingDocId)
                .then(refresh)
                .finally(() => setBusy(false));
        });
    };

    const handleTypeChange = (value) => {
        setBusy(true);
        documentServices.updateDocumentType(deliveryId, value)
            .then((payload) => {
                refresh(payload);
                // El tipo cambia el título de la hoja de acuse, así que el backend rehace los PDF
                // en segundo plano. Se avisa porque el "Ver PDF" de este momento todavía muestra
                // el anterior.
                Swal.fire({
                    icon: "info",
                    title: "Tipo actualizado",
                    text: "Los documentos se están regenerando con el nuevo título.",
                    timer: 2600,
                    showConfirmButton: false,
                });
            })
            .finally(() => setBusy(false));
    };

    const handleRegularizationSave = () => {
        setBusy(true);
        documentServices.saveRegularization(deliveryId, {
            document_type: regularizationType,
            document_number: regularizationNumber,
        })
            .then((payload) => {
                setDetail((previous) => ({ ...previous, regularization: payload.regularization }));
                if (onChanged) onChanged();
            })
            .finally(() => setBusy(false));
    };

    const handleRegenerate = () => {
        setBusy(true);
        documentServices.regenerate(deliveryId)
            .then(() => {
                Swal.fire({
                    icon: "info",
                    title: "Regenerando",
                    text: "Los documentos se están rehaciendo. Vuelve a abrir esta entrega en un momento.",
                    timer: 2600,
                    showConfirmButton: false,
                });
            })
            .finally(() => setBusy(false));
    };

    const formatValue = (value) => {
        if (!value) return "—";
        // Las fechas llegan en ISO; el resto son textos que se muestran tal cual.
        const asMoment = moment(value, moment.ISO_8601, true);
        return asMoment.isValid() ? asMoment.format("DD/MM/YYYY HH:mm") : value;
    };

    return (
        <Dialog open={Boolean(deliveryId)} onClose={onClose} maxWidth="md" fullWidth>
            <DialogTitle>
                <Stack direction="row" alignItems="center" justifyContent="space-between">
                    <Stack direction="row" alignItems="center" spacing={1.5}>
                        <Typography variant="h4" sx={{ m: 0 }}>
                            Entrega {detail?.summary?.subtitle || deliveryId}
                        </Typography>
                        {detail?.summary?.partial && (
                            <Chip size="small" color="warning" label="Entrega parcial" />
                        )}
                    </Stack>
                    <IconButton onClick={onClose} size="small"><CloseIcon /></IconButton>
                </Stack>
            </DialogTitle>

            <DialogContent dividers>
                {loading && (
                    <Stack alignItems="center" sx={{ py: 6 }}>
                        <CircularProgress color="warning" />
                    </Stack>
                )}

                {!loading && detail && (
                    <React.Fragment>
                        <Grid container spacing={1} sx={{ mb: 2 }}>
                            {(detail.summary?.sortedAttributes || []).map((attribute) => (
                                <Grid item xs={12} sm={6} key={attribute.title}>
                                    <Typography variant="caption" color="text.secondary">
                                        {attribute.title}
                                    </Typography>
                                    <Typography variant="body2">{formatValue(attribute.value)}</Typography>
                                </Grid>
                            ))}
                        </Grid>

                        {signatureStrokes && (
                            <React.Fragment>
                                <Divider sx={{ my: 2 }} />
                                <Typography variant="h5">Firma de quien recibió</Typography>
                                <Stack direction={{ xs: "column", sm: "row" }} spacing={3} alignItems="flex-start" sx={{ mt: 1 }}>
                                    {/* Se dibuja desde los trazos, que es como los guarda la app móvil, para
                                        poder descartar de un vistazo una firma vacía sin abrir el PDF. */}
                                    <SignatureCanvas signatureData={signatureStrokes} height={150} width={260} />
                                    <Stack spacing={0.5}>
                                        <Typography variant="caption" color="text.secondary">Nombre</Typography>
                                        <Typography variant="body2">{detail.receipt?.name || "—"}</Typography>
                                        <Typography variant="caption" color="text.secondary">R.U.T.</Typography>
                                        <Typography variant="body2">{detail.receipt?.tin || "—"}</Typography>
                                        {detail.receipt?.observations && (
                                            <React.Fragment>
                                                <Typography variant="caption" color="text.secondary">Observaciones</Typography>
                                                <Typography variant="body2">{detail.receipt.observations}</Typography>
                                            </React.Fragment>
                                        )}
                                    </Stack>
                                </Stack>
                            </React.Fragment>
                        )}

                        <Divider sx={{ my: 2 }} />

                        {documentEntries.length === 0 && (
                            <Alert severity="info">
                                Esta entrega todavía no tiene documentos. Se generan solos cuando el ERP
                                entrega la factura o la guía.
                            </Alert>
                        )}

                        {documentEntries.map(([title, group]) => (
                            <Box key={title} sx={{ mb: 3 }}>
                                <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1.5 }}>
                                    <Typography variant="h5" sx={{ m: 0 }}>{title}</Typography>

                                    {Boolean(group.typeOptions?.length) && (
                                        <FormControl size="small" sx={{ minWidth: 200 }}>
                                            <InputLabel id={`type-${title}`}>Tipo de documento</InputLabel>
                                            <Select
                                                labelId={`type-${title}`}
                                                label="Tipo de documento"
                                                value={group.type || ""}
                                                disabled={busy}
                                                onChange={(event) => handleTypeChange(event.target.value)}
                                            >
                                                {group.typeOptions.map((option) => (
                                                    <MenuItem key={option.value} value={option.value}>
                                                        {option.label}
                                                    </MenuItem>
                                                ))}
                                            </Select>
                                        </FormControl>
                                    )}
                                </Stack>

                                <Grid container spacing={2}>
                                    <Grid item xs={12} md={6}>
                                        <DocumentSlot
                                            title="Documento sin firmar"
                                            slotKey="original"
                                            doc={group.original}
                                            busy={busy}
                                            onUpload={handleUpload}
                                            onDelete={handleDelete}
                                        />
                                    </Grid>
                                    <Grid item xs={12} md={6}>
                                        <DocumentSlot
                                            title="Documento firmado (P.O.D.)"
                                            slotKey="signed"
                                            doc={group.signed}
                                            busy={busy}
                                            onUpload={handleUpload}
                                            onDelete={handleDelete}
                                        />
                                    </Grid>
                                </Grid>
                            </Box>
                        ))}

                        {detail.regularization?.applies && (
                            <React.Fragment>
                                <Divider sx={{ my: 2 }} />
                                <Typography variant="h5">Regularización</Typography>
                                <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
                                    Se recibió menos de lo que dice el documento. Anota con qué se cuadró.
                                </Typography>

                                <Stack direction={{ xs: "column", sm: "row" }} spacing={2} alignItems="flex-start">
                                    <FormControl size="small" sx={{ minWidth: 220 }}>
                                        <InputLabel id="regularization-type">Tipo</InputLabel>
                                        <Select
                                            labelId="regularization-type"
                                            label="Tipo"
                                            value={regularizationType}
                                            disabled={busy}
                                            onChange={(event) => setRegularizationType(event.target.value)}
                                        >
                                            {(detail.regularization.options || []).map((option) => (
                                                <MenuItem key={option.value} value={option.value}>
                                                    {option.label}
                                                </MenuItem>
                                            ))}
                                        </Select>
                                    </FormControl>

                                    <TextField
                                        size="small"
                                        label="Número de documento"
                                        value={regularizationNumber}
                                        disabled={busy}
                                        onChange={(event) => setRegularizationNumber(event.target.value)}
                                    />

                                    <Button
                                        variant="contained"
                                        disabled={busy || !regularizationType || !regularizationNumber}
                                        onClick={handleRegularizationSave}
                                    >
                                        Guardar
                                    </Button>
                                </Stack>
                            </React.Fragment>
                        )}

                        <Divider sx={{ my: 2 }} />
                        <Stack direction="row" justifyContent="flex-end">
                            <Tooltip title="Vuelve a generar los PDF desde el documento del ERP">
                                <span>
                                    <Button
                                        size="small"
                                        startIcon={<AutorenewIcon />}
                                        disabled={busy}
                                        onClick={handleRegenerate}
                                    >
                                        Regenerar documentos
                                    </Button>
                                </span>
                            </Tooltip>
                        </Stack>
                    </React.Fragment>
                )}
            </DialogContent>
        </Dialog>
    );
};

export default DeliveryDocumentsDialog;
